import moment, { Moment } from 'moment';

export const MAX_RECURRENCE_INSTANCES = 365;

export const RECURRENCE_FREQUENCIES = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  yearly: 'yearly',
} as const;

export type RecurrenceFrequency =
  typeof RECURRENCE_FREQUENCIES[keyof typeof RECURRENCE_FREQUENCIES];

export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
export type WeekdayCode = typeof WEEKDAY_CODES[number];

export const RECURRENCE_PRESETS = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  yearly: 'yearly',
  weekdays: 'weekdays',
  custom: 'custom',
} as const;

export type RecurrencePreset =
  typeof RECURRENCE_PRESETS[keyof typeof RECURRENCE_PRESETS];

export const DEFAULT_END_COUNTS = {
  day: 30,
  week: 13,
  month: 12,
  year: 5,
} as const;

export const CUSTOM_UNITS = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  yearly: 'yearly',
} as const;

export type CustomUnit = typeof CUSTOM_UNITS[keyof typeof CUSTOM_UNITS];

export function defaultEndCount(
  preset: RecurrencePreset,
  customUnit?: CustomUnit,
): number {
  if (preset === RECURRENCE_PRESETS.yearly) return DEFAULT_END_COUNTS.year;
  if (preset === RECURRENCE_PRESETS.monthly) return DEFAULT_END_COUNTS.month;
  if (
    preset === RECURRENCE_PRESETS.weekly ||
    preset === RECURRENCE_PRESETS.weekdays
  ) return DEFAULT_END_COUNTS.week;
  if (preset === RECURRENCE_PRESETS.custom) {
    if (customUnit === CUSTOM_UNITS.yearly) return DEFAULT_END_COUNTS.year;
    if (customUnit === CUSTOM_UNITS.monthly) return DEFAULT_END_COUNTS.month;
    if (customUnit === CUSTOM_UNITS.weekly) return DEFAULT_END_COUNTS.week;
    return DEFAULT_END_COUNTS.day;
  }
  return DEFAULT_END_COUNTS.day;
}

export const WEEKDAY_PRESET: WeekdayCode[] = ['MO', 'TU', 'WE', 'TH', 'FR'];

export const MONTHLY_MODES = {
  dayOfMonth: 'day_of_month',
  nthWeekday: 'nth_weekday',
} as const;

export type MonthlyMode = typeof MONTHLY_MODES[keyof typeof MONTHLY_MODES];

export const RECURRENCE_ENDS = {
  count: 'count',
  until: 'until',
} as const;

export type RecurrenceEnds = typeof RECURRENCE_ENDS[keyof typeof RECURRENCE_ENDS];

const ISO_TO_WEEKDAY: Record<number, WeekdayCode> = {
  1: 'MO',
  2: 'TU',
  3: 'WE',
  4: 'TH',
  5: 'FR',
  6: 'SA',
  7: 'SU',
};

export function weekdayCodeFromMoment(date: Moment): WeekdayCode {
  return ISO_TO_WEEKDAY[date.isoWeekday()];
}

export function getNthWeekdayOccurrence(date: Moment): { n: number; isLast: boolean } {
  const n = Math.ceil(date.date() / 7);
  const isLast = date.clone().add(1, 'week').month() !== date.month();

  return { n, isLast };
}

export function monthlyNthKey(date: Moment): 'last' | '1' | '2' | '3' | '4' | '5' {
  const { n, isLast } = getNthWeekdayOccurrence(date);
  if (isLast) return 'last';
  if (n >= 5) return '5';
  if (n === 4) return '4';
  if (n === 3) return '3';
  if (n === 2) return '2';
  return '1';
}

export interface RecurrenceFormValue {
  preset: RecurrencePreset;
  custom_unit: CustomUnit;
  interval: number | string;
  by_weekday: WeekdayCode[];
  monthly_mode: MonthlyMode;
  ends: RecurrenceEnds;
  count: number | string;
  until: Moment | null;
}

export const DEFAULT_RECURRENCE: RecurrenceFormValue = {
  preset: RECURRENCE_PRESETS.daily,
  custom_unit: CUSTOM_UNITS.daily,
  interval: 1,
  by_weekday: [],
  monthly_mode: MONTHLY_MODES.dayOfMonth,
  ends: RECURRENCE_ENDS.count,
  count: DEFAULT_END_COUNTS.day,
  until: null,
};

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  byWeekday?: WeekdayCode[];
  monthlyMode?: MonthlyMode;
  until?: Moment | string | null;
  count?: number | string | null;
}

export interface RecurrenceApiPayload {
  frequency: RecurrenceFrequency;
  interval: number;
  by_weekday?: WeekdayCode[];
  monthly_mode?: MonthlyMode;
  until?: string;
  count?: number;
}

function toInterval(interval: number | string | undefined): number {
  const parsed = Number(interval);

  return parsed >= 1 ? parsed : 1;
}

export function matchesRecurrence(date: Moment, start: Moment, rule: RecurrenceRule): boolean {
  if (date.isBefore(start, 'day')) return false;

  const interval = toInterval(rule.interval);
  const day = date.clone().startOf('day');
  const startDay = start.clone().startOf('day');

  switch (rule.frequency) {
    case RECURRENCE_FREQUENCIES.daily: {
      return day.diff(startDay, 'days') % interval === 0;
    }
    case RECURRENCE_FREQUENCIES.weekly: {
      const weekdays = rule.byWeekday?.length
        ? rule.byWeekday
        : [weekdayCodeFromMoment(start)];
      if (!weekdays.includes(weekdayCodeFromMoment(day))) return false;

      const weeks = day.clone().startOf('isoWeek').diff(startDay.clone().startOf('isoWeek'), 'weeks');

      return weeks % interval === 0;
    }
    case RECURRENCE_FREQUENCIES.monthly: {
      const months = (day.year() - startDay.year()) * 12 + (day.month() - startDay.month());
      if (months % interval !== 0) return false;

      if (rule.monthlyMode === MONTHLY_MODES.nthWeekday) {
        if (day.isoWeekday() !== startDay.isoWeekday()) return false;

        const startNth = getNthWeekdayOccurrence(startDay);
        const dateNth = getNthWeekdayOccurrence(day);

        if (startNth.isLast) return dateNth.isLast;

        return dateNth.n === startNth.n;
      }

      return day.date() === startDay.date();
    }
    case RECURRENCE_FREQUENCIES.yearly: {
      if (day.month() !== startDay.month() || day.date() !== startDay.date()) return false;

      return (day.year() - startDay.year()) % interval === 0;
    }
    default:
      return false;
  }
}

export function countRecurrenceInstances(start: Moment, rule: RecurrenceRule): {
  count: number;
  exceedsLimit: boolean;
} {
  const countLimit = rule.count == null || rule.count === ''
    ? null
    : Number(rule.count);
  const until = rule.until ? moment(rule.until).endOf('day') : null;

  if (countLimit != null && (Number.isNaN(countLimit) || countLimit < 1)) {
    return { count: 0, exceedsLimit: false };
  }

  if (countLimit != null && countLimit > MAX_RECURRENCE_INSTANCES) {
    return { count: MAX_RECURRENCE_INSTANCES, exceedsLimit: true };
  }

  if (!until && countLimit == null) {
    return { count: 0, exceedsLimit: false };
  }

  // API always inserts the start datetime first, then continues with the rule.
  let count = 1;
  if (countLimit === 1) return { count: 1, exceedsLimit: false };

  const cursor = start.clone().startOf('day').add(1, 'day');
  const maxDays = 366 * (MAX_RECURRENCE_INSTANCES + 2);

  for (let dayIndex = 0; dayIndex < maxDays; dayIndex += 1) {
    if (until && cursor.isAfter(until, 'day')) break;

    if (matchesRecurrence(cursor, start, rule)) {
      count += 1;
      if (countLimit != null && count >= countLimit) break;
      if (count > MAX_RECURRENCE_INSTANCES) {
        return { count: MAX_RECURRENCE_INSTANCES, exceedsLimit: true };
      }
    }

    cursor.add(1, 'day');
  }

  return { count, exceedsLimit: false };
}

export function resolveRecurrenceRule(
  recurrence: RecurrenceFormValue,
  startDate?: Moment | null
): RecurrenceRule {
  const interval = toInterval(recurrence.interval);
  const startWeekday = startDate ? weekdayCodeFromMoment(startDate) : undefined;
  const ends: Pick<RecurrenceRule, 'until' | 'count'> = {
    until: recurrence.ends === RECURRENCE_ENDS.until ? recurrence.until : null,
    count: recurrence.ends === RECURRENCE_ENDS.count ? recurrence.count : null,
  };

  switch (recurrence.preset) {
    case RECURRENCE_PRESETS.weekly:
      return {
        frequency: RECURRENCE_FREQUENCIES.weekly,
        interval: 1,
        byWeekday: startWeekday ? [startWeekday] : recurrence.by_weekday,
        ...ends,
      };
    case RECURRENCE_PRESETS.monthly:
      return {
        frequency: RECURRENCE_FREQUENCIES.monthly,
        interval: 1,
        monthlyMode: MONTHLY_MODES.nthWeekday,
        ...ends,
      };
    case RECURRENCE_PRESETS.yearly:
      return {
        frequency: RECURRENCE_FREQUENCIES.yearly,
        interval: 1,
        ...ends,
      };
    case RECURRENCE_PRESETS.weekdays:
      return {
        frequency: RECURRENCE_FREQUENCIES.weekly,
        interval: 1,
        byWeekday: WEEKDAY_PRESET,
        ...ends,
      };
    case RECURRENCE_PRESETS.custom: {
      if (recurrence.custom_unit === CUSTOM_UNITS.weekly) {
        return {
          frequency: RECURRENCE_FREQUENCIES.weekly,
          interval,
          byWeekday: recurrence.by_weekday,
          ...ends,
        };
      }
      if (recurrence.custom_unit === CUSTOM_UNITS.monthly) {
        return {
          frequency: RECURRENCE_FREQUENCIES.monthly,
          interval,
          monthlyMode: recurrence.monthly_mode,
          ...ends,
        };
      }
      if (recurrence.custom_unit === CUSTOM_UNITS.yearly) {
        return {
          frequency: RECURRENCE_FREQUENCIES.yearly,
          interval,
          ...ends,
        };
      }
      return {
        frequency: RECURRENCE_FREQUENCIES.daily,
        interval,
        ...ends,
      };
    }
    case RECURRENCE_PRESETS.daily:
    default:
      return {
        frequency: RECURRENCE_FREQUENCIES.daily,
        interval: 1,
        ...ends,
      };
  }
}

function getNthRecurrenceDate(
  start: Moment,
  rule: RecurrenceRule,
  occurrenceCount: number,
): Moment | null {
  if (occurrenceCount < 1) return null;

  const startDay = start.clone().startOf('day');
  if (occurrenceCount === 1) return startDay;

  let count = 1;
  const cursor = startDay.clone().add(1, 'day');
  const maxDays = 366 * (MAX_RECURRENCE_INSTANCES + 2);
  const ruleWithoutEnds: RecurrenceRule = {
    ...rule,
    until: null,
    count: null,
  };

  for (let dayIndex = 0; dayIndex < maxDays; dayIndex += 1) {
    if (matchesRecurrence(cursor, start, ruleWithoutEnds)) {
      count += 1;
      if (count >= occurrenceCount) return cursor.clone().startOf('day');
    }
    cursor.add(1, 'day');
  }

  return null;
}

export function getRecurrenceUntilDate(
  recurrence: RecurrenceFormValue,
  startDate?: Moment | null,
): Moment | null {
  if (!startDate) return null;

  const parsed = Number(recurrence.count);
  const count = parsed >= 1
    ? Math.min(Math.floor(parsed), MAX_RECURRENCE_INSTANCES)
    : defaultEndCount(recurrence.preset, recurrence.custom_unit);
  const rule = resolveRecurrenceRule({
    ...recurrence,
    ends: RECURRENCE_ENDS.count,
    count,
  }, startDate);

  return getNthRecurrenceDate(startDate, rule, count);
}

export function toRecurrencePayload(
  recurrence: RecurrenceFormValue | undefined,
  isRecurring: boolean,
  startDate?: Moment | null
): RecurrenceApiPayload | undefined {
  if (!isRecurring || !recurrence) return undefined;

  const rule = resolveRecurrenceRule(recurrence, startDate);
  const payload: RecurrenceApiPayload = {
    frequency: rule.frequency,
    interval: toInterval(rule.interval),
  };

  if (rule.frequency === RECURRENCE_FREQUENCIES.weekly) {
    payload.by_weekday = rule.byWeekday;
  }

  if (rule.frequency === RECURRENCE_FREQUENCIES.monthly) {
    payload.monthly_mode = rule.monthlyMode;
  }

  if (recurrence.ends === RECURRENCE_ENDS.count) {
    payload.count = Number(recurrence.count);
  } else if (recurrence.until) {
    payload.until = moment(recurrence.until).format('YYYY-MM-DD');
  }

  return payload;
}

export const RECURRENCE_SCOPES = {
  this: 'this',
  thisAndFollowing: 'this_and_following',
  all: 'all',
} as const;

export type RecurrenceScope =
  typeof RECURRENCE_SCOPES[keyof typeof RECURRENCE_SCOPES];

export const RECURRENCE_SCOPE_OPTIONS: RecurrenceScope[] = [
  RECURRENCE_SCOPES.this,
  RECURRENCE_SCOPES.thisAndFollowing,
  RECURRENCE_SCOPES.all,
];

export function isRecurringEvent(event: { recurrence_id?: string | null }): boolean {
  return Boolean(event.recurrence_id);
}

export function isSeriesEditScope(scope?: RecurrenceScope): boolean {
  return scope === RECURRENCE_SCOPES.all || scope === RECURRENCE_SCOPES.thisAndFollowing;
}

export function didClockTimeChange(
  next?: string | Date | null,
  previous?: string | Date | null,
): boolean {
  if (next == null && previous == null) return false;
  if (next == null || previous == null) return true;

  return moment(next).format('HH:mm') !== moment(previous).format('HH:mm');
}

export function omitUnchangedSeriesClockTimes<T extends {
  start_time?: string | Date;
  end_time?: string | Date;
}>(
  partial: T,
  nextTimes: { start_time?: string | Date | null; end_time?: string | Date | null },
  previousTimes: { start_time?: string | Date | null; end_time?: string | Date | null },
  scope?: RecurrenceScope,
): T {
  if (!isSeriesEditScope(scope)) return partial;

  const next = { ...partial };
  if (!didClockTimeChange(nextTimes.start_time, previousTimes.start_time)) {
    delete next.start_time;
  }
  if (!didClockTimeChange(nextTimes.end_time, previousTimes.end_time)) {
    delete next.end_time;
  }

  return next;
}

export function getRecurringSeriesEventIds(
  events: Record<string, { id: string; recurrence_id?: string | null; start_time?: string | null }>,
  target: { id: string; recurrence_id?: string | null; start_time?: string | null },
  scope?: RecurrenceScope,
): string[] {
  if (!scope || !target.recurrence_id) return [target.id];

  const series = Object.values(events).filter(
    (item) => item.recurrence_id === target.recurrence_id
  );

  if (scope === RECURRENCE_SCOPES.this) return [target.id];
  if (scope === RECURRENCE_SCOPES.all) return series.map((item) => item.id);

  const start = target.start_time ? moment(target.start_time) : null;

  return series
    .filter((item) => {
      if (!start || !item.start_time) return item.id === target.id;
      return !moment(item.start_time).isBefore(start);
    })
    .map((item) => item.id);
}

export function countUpcomingAndPastEventIds(
  events: Record<string, { id: string; start_time?: string | null }>,
  eventIds: string[],
  now = moment(),
): { upcomingCount: number; pastCount: number } {
  let upcomingCount = 0;
  let pastCount = 0;

  eventIds.forEach((id) => {
    const start = events[id]?.start_time;
    if (start && moment(start).isBefore(now)) pastCount += 1;
    else upcomingCount += 1;
  });

  return { upcomingCount, pastCount };
}
